import './WalletHub.css';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PayPalButtons } from "@paypal/react-paypal-js";
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { auth } from '../firebase-config'; // Import auth object
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import WalletHubSkeleton from './WalletHubSkeleton';
import HistoryTabSkeleton from './HistoryTabSkeleton';
import PayoutSetupModal from './PayoutSetupModal';
import EchoChamber from './EchoChamber';
import MessageBox from './MessageBox';
import QuestBoard from './QuestBoard';
import CreatorDashboardTab from './CreatorDashboardTab';
import LotteryPage from './LotteryPage';
import NoSpendStreakTracker from './NoSpendStreakTracker';
import { useRecurringTransactionDetector } from '../hooks/useRecurringTransactionDetector';
import RecurringTransactionPrompt from './RecurringTransactionPrompt';
import FTUEChecklist from './FTUEChecklist';
import CategoryBreakdown from './CategoryBreakdown';
import PurchaseConfirmationModal from './PurchaseConfirmationModal';
import ProSubscriptionModal from './ProSubscriptionModal';
import HarmonicSlots from './HarmonicSlots';
import { Capacitor } from '@capacitor/core';
import { IapService } from '../services/IapService';

const HubTab = ({ rewardsRef, onPlaySlots }) => {
    const { t } = useTranslation();
    const { currentUserProfile, appFunctions, setMessage, setShowProModal, handleUserSelect } = useAppContext();
    
    // --- HOOKS (Must be unconditional and at the top) ---
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [showEchoChamber, setShowEchoChamber] = useState(false);
    const [showPayoutSetupModal, setShowPayoutSetupModal] = useState(false);
    const [showConfirmWithdraw, setShowConfirmWithdraw] = useState(false);
    const [showPurchase1000Modal, setShowPurchase1000Modal] = useState(false);
    const [showPurchase2500Modal, setShowPurchase2500Modal] = useState(false);
    const [showPurchase5500Modal, setShowPurchase5500Modal] = useState(false);
    const [showPurchase12000Modal, setShowPurchase12000Modal] = useState(false);
    const [showCashoutSuccessModal, setShowCashoutSuccessModal] = useState(false);
    const [chamberTimeLeft, setChamberTimeLeft] = useState('');
    const [cashoutCooldown, setCashoutCooldown] = useState('');
    const [isCashoutAllowed, setIsCashoutAllowed] = useState(false);
    const [paypalEmail, setPaypalEmail] = useState('');
    const [showProSubscriptionModal, setShowProSubscriptionModal] = useState(false);
    const isNative = Capacitor.getPlatform() !== 'web';

    const handleRestorePurchases = async () => {
        try {
            setMessage("Restoring purchases...");
            await IapService.restorePurchases();
            setMessage("Purchases restored successfully!");
        } catch (error) {
            setMessage("Failed to restore purchases.");
        }
    };

    const handleFabClick = () => {
        rewardsRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const initialWidgets = {
        widgets: {
            'widget-1': { id: 'widget-1', content: <NoSpendStreakTracker /> },
        },
        widgetOrder: ['widget-1'],
    };

    const [widgetState, setWidgetState] = useState(() => {
        const savedOrder = localStorage.getItem('walletHubWidgetOrder');
        if (savedOrder) {
            return { ...initialWidgets, widgetOrder: JSON.parse(savedOrder) };
        }
        return initialWidgets;
    });

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const newWidgetOrder = Array.from(widgetState.widgetOrder);
        newWidgetOrder.splice(source.index, 1);
        newWidgetOrder.splice(destination.index, 0, draggableId);

        const newState = {
            ...widgetState,
            widgetOrder: newWidgetOrder,
        };
        setWidgetState(newState);
        localStorage.setItem('walletHubWidgetOrder', JSON.stringify(newWidgetOrder));
    };

    useEffect(() => {
        const checkCashoutEligibility = () => {
            if (auth.currentUser && auth.currentUser.metadata.creationTime) {
                const creationTime = new Date(auth.currentUser.metadata.creationTime).getTime();
                const oneMonth = 30 * 24 * 60 * 60 * 1000;
                const cooldownEndTime = creationTime + oneMonth;
                const now = Date.now();

                if (now < cooldownEndTime) {
                    setIsCashoutAllowed(false);
                    const interval = setInterval(() => {
                        const timeLeft = cooldownEndTime - Date.now();
                        if (timeLeft <= 0) {
                            clearInterval(interval);
                            setIsCashoutAllowed(true);
                            setCashoutCooldown('');
                        } else {
                            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                            setCashoutCooldown(`${days}d ${hours}h ${minutes}m`);
                        }
                    }, 1000);
                    return () => clearInterval(interval);
                } else {
                    setIsCashoutAllowed(true);
                }
            }
        };

        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                checkCashoutEligibility();
            }
        });

        return () => unsubscribe();
    }, []);

    const handleBuyEchoes = useCallback(async (itemId, tokens) => {
        setIsPurchasing(true);
        const createPayPalOrder = httpsCallable(appFunctions, 'createPayPalOrder');
        try {
            const result = await createPayPalOrder({ itemId, tokens });
            return result.data.orderID; 
        } catch (error) {
            setMessage(t('walletHub.purchaseError', { error: error.message || t('walletHub.paypalOrderError') }));
            throw new Error(error.message || t('walletHub.paypalOrderError'));
        }
    }, [appFunctions, setMessage, t]);

    const onApprove = useCallback(async (data) => {
        const capturePayPalOrder = httpsCallable(appFunctions, 'capturePayPalOrder');
        try {
            const result = await capturePayPalOrder({ orderID: data.orderID });
            if (result.data.success) {
                setMessage(t('walletHub.purchaseSuccess', { count: result.data.tokensAdded }));
            }
        } catch (error) {
            console.error("Error capturing PayPal order:", error);
            setMessage(t('walletHub.purchaseFailed', { message: error.message }));
        }
    }, [appFunctions, setMessage, t]);

    const performWithdraw = useCallback(async () => {
        if (!paypalEmail) {
            setMessage(t('walletHub.paypalEmailRequired'));
            return;
        }
        setShowConfirmWithdraw(false);
        const executePayPalPayout = httpsCallable(appFunctions, 'executePayPalPayout');
        try {
            const result = await executePayPalPayout({ amount: currentUserProfile?.tokens || 0, paypalEmail });
            if (result.data.success) {
                setMessage(result.data.message);
                setShowCashoutSuccessModal(true);
            }
        } catch (e) {
            console.error("Error cashing out echoes:", e);
            setMessage(t('walletHub.cashoutError', { message: e.message }));
        }
    }, [appFunctions, setMessage, currentUserProfile?.tokens, paypalEmail, t]);

    const handleWithdraw = useCallback(() => {
        if (!isCashoutAllowed) {
            setMessage(t('walletHub.cashoutCooldownMessage', { time: cashoutCooldown }));
            return;
        }
        const currentEchoes = currentUserProfile?.tokens || 0;
        const MINIMUM_WITHDRAWAL_ECHOES = 500;
        if (currentEchoes < MINIMUM_WITHDRAWAL_ECHOES) {
            setMessage(t('walletHub.minimumWithdrawalError', { count: MINIMUM_WITHDRAWAL_ECHOES }));
            return;
        }
        setShowConfirmWithdraw(true);
    }, [currentUserProfile, setMessage, isCashoutAllowed, cashoutCooldown, t]);

    // --- Computed Logic (Safe with optional chaining) ---
    const canOpenChamber = !currentUserProfile?.lastChamberOpen || 
        (currentUserProfile.lastChamberOpen?.toDate && 
         (Date.now() - currentUserProfile.lastChamberOpen.toDate().getTime()) >= 22 * 60 * 60 * 1000);

    // Effect for Chamber Timer
    useEffect(() => {
        if (!canOpenChamber && currentUserProfile?.lastChamberOpen?.toDate) {
            const interval = setInterval(() => {
                const cooldownEndTime = currentUserProfile.lastChamberOpen.toDate().getTime() + (22 * 60 * 60 * 1000);
                const timeLeftMs = cooldownEndTime - Date.now();

                if (timeLeftMs <= 0) {
                    setChamberTimeLeft('');
                    clearInterval(interval);
                } else {
                    const hours = Math.floor(timeLeftMs / (1000 * 60 * 60));
                    const minutes = Math.floor((timeLeftMs % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((timeLeftMs % (1000 * 60)) / 1000);
                    setChamberTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
                }
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [canOpenChamber, currentUserProfile?.lastChamberOpen]);

    const amountInUSD = (currentUserProfile?.tokens || 0) * 0.007;
    const confirmationMessage = (
        <div>
            <p>{t('walletHub.cashoutConfirmation', { echoes: currentUserProfile?.tokens || 0, amount: amountInUSD.toFixed(2) })}</p>
            <p>{t('walletHub.paypalEmailPrompt')}</p>
            <input 
                type="email" 
                value={paypalEmail} 
                onChange={(e) => setPaypalEmail(e.target.value)} 
                className="w-full bg-gray-900 border-b border-blue-500 text-white focus:outline-none mt-2" 
                placeholder={t('walletHub.paypalEmailPlaceholder')}
            />
        </div>
    );

    // --- CONDITIONAL RETURN GUARD (Must be after all hooks) ---
    if (!currentUserProfile) {
        return <LoadingSpinner />;
    }

    return (
        <div className="animate-fadeIn">
            {showPurchase1000Modal && <PurchaseConfirmationModal onClose={() => setShowPurchase1000Modal(false)} handleBuyEchoes={handleBuyEchoes} onApprove={onApprove} itemId="echoes-1000" price="12.99" tokens={1000} />}
            {showPurchase2500Modal && <PurchaseConfirmationModal onClose={() => setShowPurchase2500Modal(false)} handleBuyEchoes={handleBuyEchoes} onApprove={onApprove} itemId="echoes-2500" price="29.99" tokens={2500} />}
            {showPurchase5500Modal && <PurchaseConfirmationModal onClose={() => setShowPurchase5500Modal(false)} handleBuyEchoes={handleBuyEchoes} onApprove={onApprove} itemId="echoes-5500" price="59.99" tokens={5500} />}
            {showPurchase12000Modal && <PurchaseConfirmationModal onClose={() => setShowPurchase12000Modal(false)} handleBuyEchoes={handleBuyEchoes} onApprove={onApprove} itemId="echoes-12000" price="119.99" tokens={12000} />}
            {showCashoutSuccessModal && <PurchaseConfirmationModal
                onClose={() => setShowCashoutSuccessModal(false)}
                price={amountInUSD.toFixed(2)}
                tokens={currentUserProfile?.tokens || 0}
                isCashout={true}
            />}
            {showPayoutSetupModal && <PayoutSetupModal onClose={() => setShowPayoutSetupModal(false)} />}
            {showEchoChamber && <EchoChamber onClose={() => setShowEchoChamber(false)} />}
            {showConfirmWithdraw && <MessageBox message={confirmationMessage} showConfirm={true} onConfirm={performWithdraw} onClose={() => setShowConfirmWithdraw(false)} />}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
                <div className="bg-white/5 border border-white/10 p-4 rounded-xl shadow-inner"><h4 className="text-sm font-bold text-purple-300">{t('walletHub.stats.echoes')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center"><LucideIcons.Gem size={24} className="mr-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />{currentUserProfile.tokens || 0}</p></div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-xl shadow-inner"><h4 className="text-sm font-bold text-sky-300">{t('walletHub.stats.reputation')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center"><LucideIcons.Shield size={24} className="mr-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />{currentUserProfile.reputationScore || 0}</p></div>
                <div className="bg-white/5 border border-white/10 p-4 rounded-xl shadow-inner"><h4 className="text-sm font-bold text-yellow-300">{t('walletHub.stats.vibeScore')}</h4><p className="text-3xl font-bold text-white flex items-center justify-center"><LucideIcons.Smile size={24} className="mr-2 drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />{currentUserProfile.vibeScore || 0}</p></div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="widgets">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4 mb-6">
                            {widgetState.widgetOrder.map((widgetId, index) => (
                                <Draggable key={widgetId} draggableId={widgetId} index={index}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className="relative bg-white/5 border border-white/10 rounded-2xl p-4 pt-8 shadow-inner"
                                        >
                                            <div
                                                {...provided.dragHandleProps}
                                                className="absolute top-2 right-3 text-gray-500 hover:text-white cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/10 transition-all z-20"
                                                title="Drag to reorder"
                                            >
                                                <LucideIcons.GripVertical size={16} />
                                            </div>
                                            {widgetState.widgets[widgetId].content}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <div onClick={() => handleUserSelect(currentUserProfile.id)} className="glass-panel-hover bg-white/5 p-4 rounded-xl flex items-center mb-6 cursor-pointer transition-all">
                <img src={currentUserProfile.photoURL || "https://placehold.co/100x100/AEC6CF/FFFFFF?text=U"} alt={currentUserProfile.displayName} className="w-20 h-20 rounded-full object-cover border border-white/20 shadow-lg mr-4" />
                <div>
                    <h3 className="text-2xl font-bold text-white">{currentUserProfile.displayName}</h3>
                    <p className="text-md text-gray-300 italic">"{currentUserProfile.bio || t('walletHub.defaultBio')}"</p>
                </div>
            </div>
            <div ref={rewardsRef} className="mt-6 pt-6 border-t border-gray-700">
                <h3 className="text-xl font-bold mb-4 text-blue-200 font-sans">{t('walletHub.manageTitle')}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-4">
                        <h4 className="text-lg font-semibold text-center text-blue-200 mb-2">{t('walletHub.purchaseTitle')}</h4>
                        <button onClick={() => setShowPurchase1000Modal(true)} className="cosmic-button bg-blue-500/20 border-blue-400 text-blue-300 hover:bg-blue-500/40 hover:shadow-blue-400/50">
                            <LucideIcons.PlusCircle size={16} className="mr-2" />
                            <span>{t('walletHub.purchaseOptions.one')}</span>
                        </button>
                        <button onClick={() => setShowPurchase2500Modal(true)} className="cosmic-button bg-blue-500/20 border-blue-400 text-blue-300 hover:bg-blue-500/40 hover:shadow-blue-400/50">
                            <LucideIcons.PlusCircle size={16} className="mr-2" />
                            <span>{t('walletHub.purchaseOptions.two')}</span>
                        </button>
                        <button onClick={() => setShowPurchase5500Modal(true)} className="cosmic-button bg-blue-500/20 border-blue-400 text-blue-300 hover:bg-blue-500/40 hover:shadow-blue-400/50">
                            <LucideIcons.PlusCircle size={16} className="mr-2" />
                            <span>{t('walletHub.purchaseOptions.three')}</span>
                        </button>
                        <button onClick={() => setShowPurchase12000Modal(true)} className="cosmic-button bg-blue-500/20 border-blue-400 text-blue-300 hover:bg-blue-500/40 hover:shadow-blue-400/50">
                            <LucideIcons.PlusCircle size={16} className="mr-2" />
                            <span>{t('walletHub.purchaseOptions.four')}</span>
                        </button>
                    </div>
                    <div className="flex flex-col gap-4">
                        <h4 className="text-lg font-semibold text-center text-purple-200 mb-2">{t('walletHub.actionsTitle')}</h4>
                        <button 
                            onClick={handleWithdraw} 
                            disabled={!isCashoutAllowed || (currentUserProfile.tokens || 0) < 500} 
                            className="cosmic-button bg-red-500/20 border-red-400 text-red-300 hover:bg-red-500/40 hover:shadow-red-400/50 disabled:opacity-50"
                        >
                            <LucideIcons.Banknote size={16} className="mr-2" />
                            <span>{isCashoutAllowed ? t('walletHub.cashoutButton') : t('walletHub.unlocksIn', { time: cashoutCooldown })}</span>
                        </button>
                        <button onClick={() => setShowEchoChamber(true)} disabled={!canOpenChamber} className="cosmic-button bg-indigo-500/20 border-indigo-400 text-indigo-300 hover:bg-indigo-500/40 hover:shadow-indigo-400/50 disabled:opacity-50">
                            <LucideIcons.Gift size={16} className="mr-2" />
                            <span>{canOpenChamber ? t('walletHub.openChamberButton') : t('walletHub.opensIn', { time: chamberTimeLeft })}</span>
                        </button>
                        <button onClick={onPlaySlots} className="cosmic-button bg-violet-500/20 border-violet-400 text-violet-300 hover:bg-violet-500/40 hover:shadow-violet-400/50">
                            <LucideIcons.Gamepad2 size={16} className="mr-2" />
                            <span>{t('walletHub.playSlotsButton')}</span>
                        </button>
                        <button onClick={() => currentUserProfile?.proStatus === 'active' ? setShowProSubscriptionModal(true) : setShowProModal(true)} className="cosmic-button bg-amber-500/20 border-amber-400 text-amber-300 hover:bg-amber-500/40 hover:shadow-amber-400/50">
                            <LucideIcons.Crown size={16} className="mr-2" />
                            <span>{t('walletHub.viewProButton')}</span>
                        </button>
                        {isNative && (
                            <button onClick={handleRestorePurchases} className="cosmic-button bg-teal-500/20 border-teal-400 text-teal-300 hover:bg-teal-500/40 hover:shadow-teal-400/50">
                                <LucideIcons.RefreshCw size={16} className="mr-2" />
                                <span>Restore Purchases</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {showProSubscriptionModal && <ProSubscriptionModal onClose={() => setShowProSubscriptionModal(false)} />}
            <button
                onClick={handleFabClick}
                className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50"
                aria-label={t('walletHub.scrollToRewards')}
            >
                <LucideIcons.ArrowDown size={24} />
            </button>
        </div>
    );
};

const HistoryTab = () => {
    const { t } = useTranslation();
    const { userId, db, setMessage, appId, handleUserSelect } = useAppContext();
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedTxs, setSelectedTxs] = useState(new Set());
    const [showCategoryModal, setShowCategoryModal] = useState(false);

    const config = {
        QUEST_REWARD: { icon: LucideIcons.Award, color: 'text-purple-400' },
        GIFT_SENT: { icon: LucideIcons.Send, color: 'text-red-400' },
        GIFT_RECEIVED: { icon: LucideIcons.Gift, color: 'text-green-400' },
        AMPLIFY_SPENT: { icon: LucideIcons.Flame, color: 'text-red-400' },
        AMPLIFY_EARNED: { icon: LucideIcons.Flame, color: 'text-green-400' },
        STARLIGHT_SURGE_SENT: { icon: LucideIcons.Sparkles, color: 'text-red-400' },
        STARLIGHT_SURGE_RECEIVED: { icon: LucideIcons.Sparkles, color: 'text-green-400' },
        PURCHASE: { icon: LucideIcons.PlusCircle, color: 'text-green-400' },
        CASH_OUT: { icon: LucideIcons.Banknote, color: 'text-red-400' },
        PRO_STIPEND: { icon: LucideIcons.Crown, color: 'text-green-400' },
        ECHO_CHAMBER_REWARD: { icon: LucideIcons.Box, color: 'text-green-400' },
        DEFAULT: { icon: LucideIcons.CircleDollarSign, color: 'text-gray-400' },
    };

    const handleBulkCategorize = () => {
        if (selectedTxs.size === 0) return;
        setShowCategoryModal(true);
    };

    const handleCategoryChange = (category) => {
        // In a real app, this would call a cloud function to update all selected transactions.
        setMessage(t('walletHub.history.categorizedMessage', { count: selectedTxs.size, category }));
        setShowCategoryModal(false);
        handleToggleSelectMode();
    };


    const handleToggleSelectMode = () => {
        setIsSelectMode(!isSelectMode);
        setSelectedTxs(new Set());
    };

    const handleTxSelect = (txId) => {
        const newSelectedTxs = new Set(selectedTxs);
        if (newSelectedTxs.has(txId)) {
            newSelectedTxs.delete(txId);
        } else {
            newSelectedTxs.add(txId);
        }
        setSelectedTxs(newSelectedTxs);
    };

    const handleExportPdf = () => {
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", () => {
            loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", () => {
                const jsPDF = window.jspdf;
                const html2canvas = window.html2canvas;

                if (!jsPDF || !html2canvas) {
                    setMessage(t('walletHub.history.pdfError'));
                    return;
                }

                const input = document.getElementById('printable-history');
                if (!input) {
                    setMessage(t('walletHub.history.pdfError'));
                    return;
                }
                
                html2canvas(input, { scrollY: -window.scrollY, windowWidth: input.scrollWidth, windowHeight: input.scrollHeight })
                    .then((canvas) => {
                        const imgData = canvas.toDataURL('image/png');
                        const pdf = new jsPDF.jsPDF({
                            orientation: 'p',
                            unit: 'px',
                            format: 'a4'
                        });

                        const pageWidth = pdf.internal.pageSize.getWidth();
                        const pageHeight = pdf.internal.pageSize.getHeight();
                        
                        const canvasWidth = canvas.width;
                        const canvasHeight = canvas.height;
                        
                        const ratio = canvasWidth / pageWidth;
                        const imgHeight = canvasHeight / ratio;

                        let heightLeft = imgHeight;
                        let position = 0;

                        pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
                        heightLeft -= pageHeight;

                        while (heightLeft > 0) {
                            position = heightLeft - imgHeight;
                            pdf.addPage();
                            pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
                            heightLeft -= pageHeight;
                        }
                        
                        pdf.save("echoes_history.pdf");
                    });
            });
        });
    };

    const PrintableHistory = ({ transactions }) => {
        const ECHO_TO_USD_RATE = 0.007;
        const totalCashValue = transactions.reduce((sum, tx) => sum + (tx.amount * ECHO_TO_USD_RATE), 0);

        return (
            <div id="printable-history" style={{ position: 'absolute', left: '-9999px', top: '-9999px', padding: '20px', background: 'white', color: 'black', width: '800px', fontFamily: 'sans-serif' }}>
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h1 style={{ margin: '0', fontSize: '24px' }}>Health & Legend LLC</h1>
                    <h2 style={{ margin: '0', fontSize: '18px', color: '#333' }}>Transaction History Report</h2>
                    <p style={{ margin: '5px 0', fontSize: '12px', color: '#555' }}>Exported on: {new Date().toLocaleDateString()}</p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f2f2f2' }}>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Description</th>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Type</th>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Amount (Echoes)</th>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>Cash Value (USD)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.map(tx => {
                            const cashValue = tx.amount * ECHO_TO_USD_RATE;
                            return (
                                <tr key={tx.id}>
                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{tx.description}</td>
                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{tx.timestamp?.toDate().toLocaleString()}</td>
                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{tx.type}</td>
                                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right', color: tx.amount > 0 ? 'green' : 'red' }}>{tx.amount.toLocaleString()}</td>
                                    <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'right' }}>${cashValue.toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <div style={{ textAlign: 'right', marginTop: '20px', fontSize: '16px', fontWeight: 'bold' }}>
                    <p>Total Cash Value: ${totalCashValue.toFixed(2)}</p>
                </div>
                <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '12px', color: '#555' }}>
                    <p>For support, contact: inbox@whispersharmony.com</p>
                </div>
            </div>
        );
    };

    const loadScript = (src, onReady) => {
        const existingScript = document.querySelector(`script[src="${src}"]`);
        if (existingScript) {
            onReady();
            return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = onReady;
        script.onerror = () => setMessage(t('walletHub.history.exportLibraryError'));
        document.body.appendChild(script);
    };

    const handleExport = () => {
        if (transactions.length === 0) {
            setMessage(t('walletHub.history.noTransactionsToExport'));
            return;
        }

        loadScript("https://unpkg.com/papaparse@5.3.2/papaparse.min.js", () => {
            const Papa = window.Papa;
            if (!Papa) {
                setMessage(t('walletHub.history.csvError'));
                return;
            }

            const ECHO_TO_USD_RATE = 0.007;
            let totalCashValue = 0;

            const dataRows = transactions.map(tx => {
                const cashValue = tx.amount * ECHO_TO_USD_RATE;
                totalCashValue += cashValue;
                return {
                    Date: tx.timestamp?.toDate().toISOString(),
                    Description: tx.description,
                    Amount: tx.amount,
                    'Cash Value (USD)': cashValue.toFixed(2),
                    Type: tx.type,
                    Counterparty: tx.counterpartyName || 'N/A',
                };
            });

            const headerRows = [
                { 'Column 1': 'Health & Legend LLC', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': 'Transaction History Report', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': `Export Date: ${new Date().toLocaleDateString()}`,'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': '', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' }, // Empty row for spacing
            ];

            const footerRows = [
                { 'Column 1': '', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' }, // Empty row for spacing
                { 'Column 1': `Total Transactions: ${transactions.length}`,'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': `Total Cash Value: $${totalCashValue.toFixed(2)}`,'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': 'Health & Legend LLC', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
                { 'Column 1': 'For support, contact: inbox@whispersharmony.com', 'Column 2': '', 'Column 3': '', 'Column 4': '', 'Column 5': '', 'Column 6': '' },
            ];

            const csvHeader = Papa.unparse(headerRows, { header: false });
            const csvData = Papa.unparse(dataRows);
            const csvFooter = Papa.unparse(footerRows, { header: false });

            const finalCsv = `${csvHeader}
${csvData}
${csvFooter}`;

            const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'echoes_history.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    };


    useEffect(() => {
        if (!userId || !db) return;
        const transQuery = query(collection(db, `artifacts/${appId}/users/${userId}/transactions`), orderBy("timestamp", "desc"), limit(50));
        const unsubscribe = onSnapshot(transQuery, (snapshot) => {
            setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching transaction history:", error);
            setMessage(t('walletHub.history.loadError'));
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [userId, db, appId, collection, query, orderBy, limit, onSnapshot, setMessage, t]);

    const TransactionCard = ({ tx }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [editDescription, setEditDescription] = useState(tx.description);
        const [editAmount, setEditAmount] = useState(tx.amount);

        const handleSave = () => {
            // In a real app, this would update the transaction in Firestore
            setMessage(t('walletHub.history.transactionUpdated', { description: editDescription }));
            setIsEditing(false);
            // Here you would call a function to update the firestore doc with id tx.id
        };


        const { icon: Icon, color } = config[tx.type] || config.DEFAULT;
        const isCredit = tx.amount > 0;

        const handleNavigate = () => {
            if (tx.counterpartyId) {
                handleUserSelect(tx.counterpartyId);
            }
        };

        return (
            <div className={`bg-white/5 border border-white/10 p-3 rounded-lg flex items-center gap-4 transition-all duration-200 ${isSelectMode ? 'pl-2' : 'hover:scale-[1.02] hover:bg-white/10'} ${selectedTxs.has(tx.id) ? 'bg-blue-900/50' : ''}`}>
                {isSelectMode && (
                    <input 
                        type="checkbox"
                        checked={selectedTxs.has(tx.id)}
                        onChange={() => handleTxSelect(tx.id)}
                        className="form-checkbox h-5 w-5 text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 ml-2"
                    />
                )}
                <Icon size={24} className={`flex-shrink-0 ${color}`} />
                <div className="flex-grow">
                    {isEditing ? (
                        <input 
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full bg-gray-900 border-b border-blue-500 text-white focus:outline-none"
                        />
                    ) : (
                        <p className="font-semibold text-white">{tx.description}</p>
                    )}
                    {tx.counterpartyName && (
                        <p onClick={handleNavigate} className="text-xs text-gray-400 cursor-pointer hover:underline">
                            {isCredit ? t('walletHub.history.from') : t('walletHub.history.to')} {tx.counterpartyName}
                        </p>
                    )}
                    <p className="text-xs text-gray-500">{tx.timestamp?.toDate().toLocaleString()}</p>
                </div>
                {isEditing ? (
                    <input 
                        type="number"
                        value={editAmount}
                        onChange={(e) => setEditAmount(parseFloat(e.target.value) || 0)}
                        className="w-24 bg-gray-900 border-b border-blue-500 text-white font-bold text-lg focus:outline-none text-right"
                    />
                ) : (
                    <p className={`font-bold text-lg flex-shrink-0 ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
                        {isCredit ? '+' : ''}{tx.amount.toLocaleString()}
                    </p>
                )}
                <div className="flex gap-1">
                    {isEditing ? (
                        <button onClick={handleSave} aria-label={t('walletHub.history.saveAriaLabel')} className="p-1 text-green-400 hover:bg-gray-700 rounded-full"><LucideIcons.Check size={16} /></button>
                    ) : (
                        <button onClick={() => setIsEditing(true)} aria-label={t('walletHub.history.editAriaLabel')} className="p-1 text-gray-400 hover:bg-gray-700 rounded-full"><LucideIcons.Pencil size={16} /></button>
                    )}
                </div>
            </div>
        );
    };

    if (isLoading) {
        return <HistoryTabSkeleton />;
    }

    const filteredTransactions = transactions.filter(tx => 
        tx.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="animate-fadeIn space-y-3">
            <PrintableHistory transactions={filteredTransactions} />
            <div className="flex justify-between items-center mb-2 gap-2">
                <div className="relative flex-grow">
                    <LucideIcons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                        type="text"
                        placeholder={t('walletHub.history.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-full py-1.5 pl-8 pr-3 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                </div>
                <button onClick={handleToggleSelectMode} className={`small-action-button text-white flex-shrink-0 ${isSelectMode ? 'bg-red-600' : 'bg-blue-600'}`}>
                    {isSelectMode ? t('walletHub.history.cancel') : t('walletHub.history.select')}
                </button>
                <button onClick={handleExport} className="small-action-button bg-gray-600 hover:bg-gray-700 text-white flex-shrink-0">
                    <LucideIcons.Download size={14} className="mr-2" />
                    CSV
                </button>
                <button onClick={handleExportPdf} className="small-action-button bg-gray-600 hover:bg-gray-700 text-white flex-shrink-0">
                    <LucideIcons.FileText size={14} className="mr-2" />
                    PDF
                </button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto custom-scrollbar pr-2 space-y-3">
                {filteredTransactions.length > 0 ? (
                    filteredTransactions.map(tx => <TransactionCard key={tx.id} tx={tx} />)
                ) : (
                    <div className="text-center text-gray-400 italic py-8">
                        <LucideIcons.SearchX size={48} className="mx-auto mb-4" />
                        <p>{t('walletHub.history.noResults')}</p>
                    </div>
                )}
            </div>

            {isSelectMode && selectedTxs.size > 0 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-full max-w-md p-2">
                    <div className="bg-blue-800/90 backdrop-blur-sm rounded-lg shadow-lg p-2 flex justify-between items-center animate-fadeInUp">
                        <p className="text-sm font-bold text-white ml-2">{t('walletHub.history.selected', { count: selectedTxs.size })}</p>
                        <button onClick={handleBulkCategorize} className="small-action-button bg-blue-500 hover:bg-blue-600 text-white">
                            <LucideIcons.Tags size={14} className="mr-2" />
                            {t('walletHub.history.categorize')}
                        </button>
                    </div>
                </div>
            )}

            {showCategoryModal && (
                <div className="modal-overlay">
                    <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full">
                        <h3 className="text-lg font-bold mb-4">{t('walletHub.history.chooseCategory')}</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.keys(config).filter(c => c !== 'DEFAULT').map(category => (
                                <button key={category} onClick={() => handleCategoryChange(category)} className="small-action-button bg-gray-700 hover:bg-gray-600 justify-start">
                                    {category.replace(/_/g, ' ').toLowerCase()}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setShowCategoryModal(false)} className="w-full mt-4 small-action-button bg-red-600 hover:bg-red-700">{t('walletHub.history.cancel')}</button>
                    </div>
                </div>
            )}
        </div>
    );
};
function WalletHub({ initialTab = 'hub' }) {
    const { t } = useTranslation();
    const { currentUserProfile, setMessage, appFunctions } = useAppContext();
    const [activeTab, setActiveTab] = useState(initialTab);
    const { potentialRecurring, setPotentialRecurring } = useRecurringTransactionDetector();
    const rewardsRef = useRef(null);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const spinHarmonicSlotsCallable = httpsCallable(appFunctions, 'spinHarmonicSlots');

    const handleSpin = useCallback(async (currentBet, debugResult = null) => {
        try {
            const optimisticBalance = currentUserProfile.tokens - currentBet;
            if (optimisticBalance < 0) {
                return { success: false };
            }
            const payload = { currentBet };
            if (debugResult) payload.debugResult = debugResult;

            const result = await spinHarmonicSlotsCallable(payload);
            if (result.data.success) {
                return { 
                    success: true, 
                    newBalance: result.data.newBalance, 
                    winnings: result.data.winnings, 
                    jackpotTriggered: result.data.jackpotTriggered, 
                    finalGrid: result.data.finalGrid, 
                    newJackpotPool: result.data.newJackpotPool 
                };
            } else {
                setMessage(result.data.message || t('walletHub.slots.spinError')); // Keep generic error message
                return { success: false };
            }
        } catch (error) {
            console.error("Error spinning Harmonic Slots:", error);
            setMessage(t('walletHub.slots.spinError', { message: error.message }));
            return { success: false };
        }
    }, [currentUserProfile, spinHarmonicSlotsCallable, setMessage, t]);

    const handleConfirmRecurring = () => {
        setMessage(t('walletHub.recurring.confirm', { description: potentialRecurring.description }));
        setPotentialRecurring(null);
    };

    const handleDismissRecurring = () => {
        setPotentialRecurring(null);
    };

    const reputationScore = currentUserProfile?.reputationScore || 0;
    const canSeeDashboard = reputationScore >= 250;
    const tabs = ['hub', 'quests', 'history', 'breakdown', 'lottery', 'slots'];
    if (canSeeDashboard) tabs.push('dashboard');

    const handleTabKeyDown = (e) => {
        const currentIndex = tabs.indexOf(activeTab);
        if (e.key === 'ArrowRight') {
            const nextIndex = (currentIndex + 1) % tabs.length;
            setActiveTab(tabs[nextIndex]);
        } else if (e.key === 'ArrowLeft') {
            const nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            setActiveTab(tabs[nextIndex]);
        }
    };

    useEffect(() => {
        const activeTabElement = document.getElementById(`tab-${activeTab}`);
        activeTabElement?.focus();
    }, [activeTab]);

    if (!currentUserProfile) {
        return <WalletHubSkeleton />;
    }

    return (
        <div className="p-4 sm:p-6 glass-panel max-w-3xl mx-auto text-gray-100">
            <FTUEChecklist activeTab={activeTab} />
            
            <div role="tablist" aria-label={t('walletHub.ariaLabel')} onKeyDown={handleTabKeyDown} className="flex flex-wrap justify-center border-b border-gray-700 mb-6">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        id={`tab-${tab}`}
                        role="tab"
                        aria-selected={activeTab === tab}
                        aria-controls={`panel-${tab}`}
                        tabIndex={activeTab === tab ? 0 : -1}
                        onClick={() => setActiveTab(tab)}
                        className={`profile-tab-button ${activeTab === tab ? 'active' : ''}`}>
                        {t(`walletHub.tabs.${tab}`)}
                    </button>
                ))}
            </div>

            <div id={`panel-${activeTab}`} role="tabpanel" className="page-container">
                {activeTab === 'hub' && <HubTab onPlaySlots={() => setActiveTab('slots')} rewardsRef={rewardsRef} />} 
                {activeTab === 'quests' && <QuestBoard />}
                {activeTab === 'history' && <HistoryTab />}
                {activeTab === 'breakdown' && <CategoryBreakdown />}
                {activeTab === 'lottery' && <LotteryPage />}
                {activeTab === 'slots' && (
                    <HarmonicSlots
                        userBalance={currentUserProfile.tokens || 0}
                        onSpin={handleSpin}
                        onClose={() => setActiveTab('hub')}
                    />
                )} 
                {activeTab === 'dashboard' && canSeeDashboard && <CreatorDashboardTab />}
            </div>

            <RecurringTransactionPrompt 
                suggestion={potentialRecurring}
                onConfirm={handleConfirmRecurring}
                onDismiss={handleDismissRecurring}
            />
        </div>
    );
}
export default WalletHub;